// Client for the baba-v2 mobile backend gateway (translate.itsbaba.com/api/v1).
// Phase 1 of the web<->mobile identity bridge: the SAME web session JWT the
// web app already sends to e.itsbaba.com (see baba-auth.ts / authedFetch in
// baba-api.ts) also authenticates against the gateway, which resolves it to a
// stable mobile identity (issuer 'baba_web' in the baba-v2 repo's
// server/gateway/auth.ts).
//
// Phase 2: useGatewayMe relays that identity's `stable_user_id` (present on the
// /me payload for baba_web callers — see the gateway's webIdentity.ts
// `withStableUserIdForWebCaller`) to baba api's POST /api/extension/account-
// link/gateway-sid, so a LATER mobile sign-in with this account's email can
// find it already recorded and become an alias of THIS identity instead of
// minting a second canonical one. See the gateway repo's
// neon/migrations/20260830120000_web_identity_link.sql follow-up note.
import { useCallback, useEffect, useState } from 'react'
import { getAuthToken } from './baba-auth'

function normalizeEnvUrl(v: string | undefined): string {
  if (v == null || v === '') return ''
  return v.trim().replace(/^["']|["']$/g, '').replace(/\/+$/, '')
}

const rawGatewayBase = normalizeEnvUrl(import.meta.env.VITE_BABA_GATEWAY_URL as string | undefined)

export const BABA_GATEWAY_BASE_URL = rawGatewayBase || 'https://translate.itsbaba.com/api/v1'

// Mirrors apps/web/lib/baba-api.ts's BABA_API_BASE_URL resolution, kept as a
// local, self-contained copy rather than an import: this module's test
// harness (gateway.test.mjs) only stubs its OWN direct dependencies
// (baba-auth, react), and baba-api.ts pulls in analytics.ts and a much larger
// surface that has no reason to be exercised by a gateway-client test.
const rawBabaApiBase = normalizeEnvUrl(import.meta.env.VITE_BABA_API_BASE_URL as string | undefined)
const LOCAL_WORKER_RE = /^https?:\/\/(localhost|127\.0\.0\.1):8787\/?$/i
const PROD_WORKER_RE = /^https:\/\/e\.itsbaba\.com\/?$/i
export const BABA_API_BASE_URL_FOR_GATEWAY_SID =
  import.meta.env.DEV && (rawBabaApiBase === '' || LOCAL_WORKER_RE.test(rawBabaApiBase) || PROD_WORKER_RE.test(rawBabaApiBase))
    ? ''
    : rawBabaApiBase || 'https://e.itsbaba.com'

/** sessionStorage flag so a repeat /me fetch in the same tab session doesn't re-push. */
const GATEWAY_SID_PUSHED_KEY = 'baba_gateway_sid_pushed'

/** The push in flight, shared: the identity relay and the entitlement sync
 *  (gatewayEntitlement.ts, which must wait for it) both ask on the same load. */
let sidPushInFlight: { stableUserId: string; promise: Promise<void> } | null = null

/**
 * Push this session's gateway-minted stable_user_id to baba api, once per
 * browser tab session per sid. Fire-and-forget by design: a dropped push just
 * means the NEXT /me fetch (next mount, next session) tries again — nothing
 * here blocks or fails the /me result the UI actually renders.
 */
export async function pushGatewaySid(stableUserId: string): Promise<void> {
  // Bare `sessionStorage`, not `window.sessionStorage` — matches baba-auth.ts's
  // `typeof document === 'undefined'` convention (both are equally global in a
  // real browser; this is what the test harness stubs).
  if (typeof sessionStorage === 'undefined') return
  if (sessionStorage.getItem(GATEWAY_SID_PUSHED_KEY) === stableUserId) return
  const token = getAuthToken()
  if (!token) return
  if (sidPushInFlight?.stableUserId === stableUserId) return sidPushInFlight.promise
  const promise = (async () => {
    try {
      const response = await fetch(`${BABA_API_BASE_URL_FOR_GATEWAY_SID}/api/extension/account-link/gateway-sid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stable_user_id: stableUserId }),
      })
      if (response.ok) sessionStorage.setItem(GATEWAY_SID_PUSHED_KEY, stableUserId)
    } catch {
      // Best-effort; see the doc comment above.
    } finally {
      if (sidPushInFlight?.stableUserId === stableUserId) sidPushInFlight = null
    }
  })()
  sidPushInFlight = { stableUserId, promise }
  return promise
}

/** Shape of the gateway's `{ error: { message, code } }` error body (respond.ts `error()`). */
interface GatewayErrorBody {
  error?: { message?: string; code?: string | null }
}

export class GatewayError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(message: string, status: number, code: string | null = null) {
    super(message)
    this.name = 'GatewayError'
    this.status = status
    this.code = code
  }
}

/** Thrown when the caller has no web session, or the gateway rejects the token (401). */
export class GatewayAuthError extends GatewayError {
  constructor(message = 'Not signed in', code: string | null = null) {
    super(message, 401, code)
    this.name = 'GatewayAuthError'
  }
}

async function parseGatewayError(response: Response): Promise<GatewayError> {
  let message = `Gateway request failed (${response.status})`
  let code: string | null = null
  try {
    const body = (await response.json()) as GatewayErrorBody
    if (body?.error?.message) message = body.error.message
    if (body?.error?.code !== undefined) code = body.error.code ?? null
  } catch {
    // Body wasn't JSON (or was empty) — keep the generic message.
  }
  return response.status === 401 ? new GatewayAuthError(message, code) : new GatewayError(message, response.status, code)
}

/**
 * Raw fetch against the gateway, with the web JWT attached. Throws
 * `GatewayAuthError` up front when there is no session at all, so callers
 * never send an unauthenticated request just to learn that from a 401.
 */
export async function gatewayFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getAuthToken()
  if (!token) throw new GatewayAuthError('No baba session - sign in first')
  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${token}`)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')
  return fetch(`${BABA_GATEWAY_BASE_URL}${path}`, { ...init, headers })
}

/** `gatewayFetch`, parsed as JSON and throwing a typed `GatewayError` on a non-2xx response. */
export async function gatewayFetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  // A PATCH or DELETE of the account makes the shared /me answer below stale.
  if (path === '/me' && init.method && init.method.toUpperCase() !== 'GET') invalidateGatewayMe()
  const response = await gatewayFetch(path, init)
  if (!response.ok) throw await parseGatewayError(response)
  return (await response.json()) as T
}

export interface GatewayMe {
  id: string
  /** Present only for a baba_web caller — see withStableUserIdForWebCaller in the gateway repo. */
  stable_user_id?: string
  [key: string]: unknown
}

/**
 * How long one `GET /me` answer is shared. The identity relay, the plan meter,
 * the progress pull and the settings pull all read the same row within the
 * first second of a page load, and the rail re-mounts its plan card on every
 * expand; they used to send four or five requests for one answer.
 */
const ME_CACHE_MS = 60_000

let meCache: { token: string; at: number; value: GatewayMe } | null = null
let meInFlight: { token: string; generation: number; promise: Promise<GatewayMe> } | null = null
let meGeneration = 0
let meInvalidationListening = false

/** Drop the shared /me answer. Anything that charges usage makes it stale. */
export function invalidateGatewayMe(): void {
  meCache = null
  meGeneration += 1
}

/**
 * `GET /me`, shared: one request in flight at a time, and its answer reused
 * for ME_CACHE_MS. Keyed on the session token, so a different sign-in never
 * reads the previous account's row. `fresh` skips the cache (not an in-flight
 * request started after the last invalidation).
 */
export function fetchGatewayMe(opts: { fresh?: boolean } = {}): Promise<GatewayMe> {
  const token = getAuthToken()
  if (!token) return Promise.reject(new GatewayAuthError('No baba session - sign in first'))
  if (!meInvalidationListening && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    // Every finished translation publishes its usage through this event
    // (translator/translationUsage.ts); the counters on /me moved with it.
    window.addEventListener('baba:usage-updated', invalidateGatewayMe)
    meInvalidationListening = true
  }
  if (!opts.fresh && meCache && meCache.token === token && Date.now() - meCache.at < ME_CACHE_MS) {
    return Promise.resolve(meCache.value)
  }
  if (meInFlight && meInFlight.token === token && meInFlight.generation === meGeneration) return meInFlight.promise
  const generation = meGeneration
  const promise = gatewayFetchJson<GatewayMe>('/me')
    .then((value) => {
      if (generation === meGeneration) meCache = { token, at: Date.now(), value }
      return value
    })
    .finally(() => {
      if (meInFlight?.promise === promise) meInFlight = null
    })
  meInFlight = { token, generation, promise }
  return promise
}

export interface UseGatewayMeResult {
  data: GatewayMe | null
  loading: boolean
  error: GatewayError | null
  refetch: () => void
}

/**
 * Plain hook (not RTK Query — see apps/web/src/store/babaApi.ts for that
 * pattern; this stays self-contained rather than adding a new store slice
 * before anything consumes it) fetching `GET /me` from the gateway.
 *
 * Never throws: `GatewayAuthError` for a signed-out caller lands in `error`
 * exactly like any other gateway failure, so a consumer can render a single
 * loading/error/data switch without a separate "not signed in" branch.
 */
export function useGatewayMe(): UseGatewayMeResult {
  const [data, setData] = useState<GatewayMe | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<GatewayError | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchGatewayMe({ fresh: nonce > 0 })
      .then((result) => {
        if (!cancelled) setData(result)
        if (typeof result.stable_user_id === 'string' && result.stable_user_id) {
          void pushGatewaySid(result.stable_user_id)
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof GatewayError ? e : new GatewayError(e instanceof Error ? e.message : String(e), 0))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [nonce])

  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  return { data, loading, error, refetch }
}
